<script setup lang="ts">
  import { useModalStack as modalStackAPI } from '@/common/components/modal/ModalStack.api'
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'

  const emit = defineEmits(['refetch', 'close'])
  const props = defineProps({
    title: {
      type: String,
      default: ''
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    copying: {
      type: Boolean,
      default: false
    },
    useModalStack: {
      type: Boolean,
      default: true
    }
  })

  const router = useRouter()

  const { close: closeModalStack } = modalStackAPI(router)

  function close () {
    if (props.useModalStack) {
      closeModalStack()
    } else {
      emit('close')
    }
  }

  const hasFormErrors = computed(() =>
    props.errors.form && Object.keys(props.errors.form).length > 0
  )
  const hasQueryErrors = computed(() =>
    props.errors.query && Object.keys(props.errors.query).length > 0
  )
  const hasMutationErrors = computed(() =>
    props.errors.mutation && Object.keys(props.errors.mutation).length > 0
  )
</script>

<template>
  <div class="h-full modal-frame flex flex-col">
    <div
      class="flex grow-0 shrink-0 bg-white items-center justify-between sticky top-0 border-b-4 border-transparent shadow z-40"
      :class="{ 'border-red-400': hasFormErrors || hasMutationErrors, 'border-yellow-500': copying && !hasFormErrors }"
    >
      <div class="flex-1">
        <a
          href="#"
          class="inline-block px-8 py-4 text-2xl cursor-pointer text-center hover:bg-gray-50"
          @click.prevent="close"
        >
          &larr;
        </a>
      </div>
      <div class="flex-1 text-center">
        {{ title }}
      </div>
      <div class="flex-1 flex items-center justify-end">
        <div class="mx-8 flex items-center actions">
          <slot v-if="!errors.query" name="actions" />
        </div>
      </div>
    </div>

    <slot name="before-content" />

    <div class="flex-1 overflow-y-auto bg-gray-50">
      <div class="p-8">
        <div
          v-if="hasQueryErrors"
          class="flex flex-col justify-center items-center bg-red-100 border border-red-500 p-8 text-red-900 shadow rounded text-base"
        >
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="fill-current text-red-300 w-20 h-20">
              <path
                d="M507.113 428.415L287.215 47.541C280.7 36.256 269.031 29.519 256 29.519c-13.031 0-24.7 6.737-31.215 18.022L4.887 428.415c-6.516 11.285-6.516 24.76 0 36.044 6.515 11.285 18.184 18.022 31.215 18.022h439.796c13.031 0 24.7-6.737 31.215-18.022 6.516-11.284 6.516-24.759 0-36.044zm-26.012 21.026c-.647 1.122-2.186 3.004-5.202 3.004H36.102c-3.018 0-4.556-1.881-5.202-3.004-.647-1.121-1.509-3.394 0-6.007L250.797 62.559c1.509-2.613 3.907-3.004 5.202-3.004 1.296 0 3.694.39 5.202 3.004L481.1 443.434c1.51 2.613.648 4.886.001 6.007z"
              />
              <path d="M240.987 166.095h30.037v160.197h-30.037z" />
              <circle cx="256.005" cy="376.354" r="20.025" />
            </svg>
          </div>
          <div class="mt-8">
            {{ $t('common.errors.forms.query') }}
          </div>
          <ActionButton type="danger" class="mt-8" @click="$emit('refetch')">
            {{ $t('common.actions.retry') }}
          </ActionButton>
        </div>

        <Callout
          v-else-if="hasMutationErrors && !hasFormErrors && !hasQueryErrors"
          type="error"
          text="common.errors.forms.mutation"
          class="mb-8"
        />

        <div
          v-if="copying"
          class="bg-yellow-100 text-yellow-800 p-4 border border-yellow-600 text-xs rounded shadow mb-8"
        >
          <div class="flex items-center">
            <IconCopy class="text-yellow-800 w-5 mr-4" />
            <strong class="inline-block mr-4">{{ $t('common.forms.copying_title') }}</strong>
            {{ $t('common.forms.copying_text') }}
          </div>
        </div>

        <slot v-if="!hasQueryErrors" />
      </div>
    </div>
  </div>
</template>

<style>
  .modal-frame .actions .button {
    @apply ml-4
  }
</style>
